<script setup lang="ts">
import { computed, inject, ref } from "vue";
import {
  FormResponseState,
  formResponseInjectKey,
} from "~/NewFormBuilder/composables/useFormResponse";
import { FormElementType, MoneyFormElement } from "~/api";
import PriceInput from "../PriceInput.vue";
import { DefaultFormComponentProps } from "./FormComponentRenderer.vue";

const props = defineProps<DefaultFormComponentProps<MoneyFormElement>>();
const { useAnswer } = inject(
  formResponseInjectKey,
  // Cast the type since we know it is always provided. For more info see:
  // https://vuejs.org/guide/typescript/composition-api.html#typing-provide-inject
) as FormResponseState;
const answer = useAnswer<FormElementType.MONEY>(props.modelValue);
const elementProperties = computed(() => props.modelValue.properties);

const errorMsg = ref<string | null>(null);

const handleUpdateAnswer = (newValue?: number) => {
  if (
    newValue !== undefined &&
    elementProperties.value?.min !== undefined &&
    elementProperties.value?.min !== null &&
    newValue < elementProperties.value?.min
  ) {
    errorMsg.value = `Error Saving: Must be greater than or equal to ${elementProperties.value?.min}`;
    return;
  } else if (
    newValue !== undefined &&
    elementProperties.value?.max !== undefined &&
    elementProperties.value?.max !== null &&
    newValue > elementProperties.value?.max
  ) {
    errorMsg.value = `Error Saving: Must be less than or equal to ${elementProperties.value?.max}`;
    return;
  }

  errorMsg.value = null;

  if (Number.isNaN(newValue)) {
    // If we couldn't parse, just set to undefined (eg. empty input)
    answer.value = undefined;
  } else {
    answer.value = newValue;
  }
};
</script>

<template id="form-money-component">
  <div class="MoneyComponent">
    <slot name="label" :element="modelValue" />
    <div class="money-field">
      <PriceInput
        :model-value="answer"
        :class="{ invalid: errorMsg }"
        :precision="2"
        :controls="false"
        :disabled="isReadOnly"
        :enable-copy="elementProperties.enableCopy ?? true"
        @update:model-value="handleUpdateAnswer"
      >
        <template #prepend>
          {{ elementProperties.currency }}
        </template>
      </PriceInput>
    </div>
  </div>
  <div class="money-component-error-msg">{{ errorMsg }}</div>
</template>
<style lang="less">
.MoneyComponent {
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
  justify-content: space-between;

  .money-field {
    display: flex;
    gap: 10px;
  }

  .invalid {
    --el-input-border-color: var(--el-color-danger);
    --el-border-color: var(--el-color-danger);
  }
  div.el-input {
    width: var(--standard-input-width);
    .el-input-group__prepend {
      transition: var(--el-transition-border);
      border: solid 2px var(--el-input-border-color, var(--el-border-color));
      border-right: none;
      box-sizing: border-box;
    }
    &.is-disabled {
      div.el-input-group__prepend {
        color: var(--el-disabled-text-color);
        border: solid 1px var(--el-disabled-border-color);
        border-right: none;
      }
    }
  }
}

.money-component-error-msg {
  color: var(--el-color-danger);
  font-size: 0.9rem;
  font-style: italic;
  text-align: right;
}

body.forPdf {
  .MoneyComponent {
    .pdf-span {
      min-width: 0;
      text-align: right;
      padding-right: 0.6em;
    }

    div.el-input.is-disabled div.el-input-group__prepend {
      background: none;
      border: none;
    }
  }
}
</style>
